import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from '../../../pdc-platform/src/ts/Glob';
import { IDataEditValues } from '../tsx/dataEdit/Interfaces';
import { DialogDefaultSizeType, ISize } from './Interfaces';
import { CONFIGURATION_CHANGE_DATE_FIELD } from './WidgetBase';

import ParameterFieldBase, {
    IParameterFieldBaseOptions,
    IValidator
} from '../../../_common/platform/ts/ParameterFieldBase';

export interface IParameters {
    [fieldName: string]: ParameterFieldBase<IParameterFieldBaseOptions>;
}

export interface ILayoutContentRow {
    title?: ((values: IDataEditValues) => string) | string;
    span?: string | number;
    field?: string;
    placeholder?: boolean;
    reserveSpace?: boolean;
    b?: boolean;
    i?: boolean;
    u?: boolean;
    hidden?: ((values: IDataEditValues) => boolean) | boolean;
    id?: number;
}

export interface ILayoutGroup {
    name?: string;
    rows?: (ILayoutContentRow[] | string)[];
    hidden?: ((values: IDataEditValues) => boolean) | boolean;
}

export interface ILayoutTab extends ILayoutGroup {
    groups?: ILayoutGroup[];
    singleMode?: boolean;
}

export interface IParamsLayout {
    tabs?: ILayoutTab[];
    groups?: ILayoutGroup[];
    templates?: { [type: string]: string };
    defaultWidgetEditSize?: ISize<DialogDefaultSizeType>;
}

export interface IParamsValuesEditField {
    name?: string;
    customID?: string;
}

export function validateParameters(
    values: IDataEditValues,
    parameters: IParameters,
    ignoreValidators?: IValidator[]
): { isValid: boolean; error?: string } {
    if (values == null) {
        return { isValid: false };
    }

    const keys = _.keys(parameters);

    let isValid = true;
    let error: string;

    for (let key of keys) {
        const value = values[key];
        const parameter = parameters[key];

        if (parameter != null) {
            const validationResultObj = parameter.validate(value, values, ignoreValidators);

            isValid = isValid && validationResultObj.result;

            if (!isValid) {
                error = `Parameter: '${parameter.title}', Error(s): '${validationResultObj.errors}'`;
                break;
            }
        }
    }

    return { isValid: isValid, error: error };
}

export default class ParamsValuesEdit {
    public parameters: IParameters;
    public paramsLayout: IParamsLayout;
    public layoutTemplateName: string;
    public values: IDataEditValues;
    public valuesToSave: IDataEditValues;
    public fields: IParamsValuesEditField[];
    public glob: IGlob;
    public isInvalid: boolean;

    constructor(private ignoreValidators?: IValidator[], private specialNullValidation?: boolean) {}

    public getDefault() {
        const result: IDataEditValues = {};

        if (this.parameters != null) {
            for (let fieldName in this.parameters) {
                const parameter = this.parameters[fieldName];

                if (parameter != null && parameter.getDefault != null) {
                    result[fieldName] = parameter.getDefault();
                }
            }
        }

        return result;
    }

    public fixValue(fieldName: string) {
        const parameter = this.parameters[fieldName];

        if (parameter.errors.length === 0) {
            return;
        }

        let value = this.values[fieldName];

        value = parameter.fix(value, this.values);

        if (value === value) {
            console.log(`Parameter '${parameter.title ?? fieldName}' cannot be fixed automatically.`);
            return;
        }

        parameter.validate(value, this.values);

        if (parameter.errors.length > 0) {
            value = parameter.getDefault();
        }

        this.values[fieldName] = value;

        if (this.valuesToSave != null && typeof this.valuesToSave[fieldName] !== 'undefined') {
            this.valuesToSave[fieldName] = value;
        }
    }

    public fixValues() {
        if (this.parameters != null) {
            for (let fieldName in this.parameters) {
                this.fixValue(fieldName);
            }
        }
    }

    public validate() {
        const result = validateParameters(this.values, this.parameters, this.ignoreValidators);

        this.isInvalid = !result.isValid;
        return result;
    }

    public getChangedValues(prevValues, newValues) {
        const pick = _.filter(
            _.keys(newValues),
            (key) => typeof prevValues[key] === 'undefined' || prevValues[key] !== newValues[key]
        );

        return _.pick.apply(_, [newValues].concat(pick));
    }

    private _setFieldValue(fieldName: string, value: any) {
        if (this.values == null) {
            return false;
        }

        const param = this.parameters[fieldName];

        if (param == null) {
            return false;
        }

        const obj = param.validate(value, this.values);
        const configurationChangeDate = new Date().toISOString();

        this.values[fieldName] = value;
        this.values[CONFIGURATION_CHANGE_DATE_FIELD] = configurationChangeDate;

        const valuesToSave = {
            [fieldName]: value,
            configurationChangeDate: configurationChangeDate
        };

        if (obj.result === true) {
            const newValues = param.onChange(_.clone(this.values), fieldName, value);
            const changedValues = this.getChangedValues(this.values, newValues);

            _.extend(valuesToSave, changedValues);
            this.values = newValues;
        }

        if (this.valuesToSave != null) {
            _.extend(this.valuesToSave, valuesToSave);
        }

        return obj.result;
    }

    public setFieldValue(fieldName: string, value: any) {
        const valid = this._setFieldValue(fieldName, value);

        this.validate();

        return {
            isValid: valid,
            values: this.values,
            valuesToSave: this.valuesToSave
        };
    }

    public setFieldValues(values) {
        const valid = _.map(_.keys(values), (key) => {
            return this._setFieldValue(key, values[key]);
        });

        this.validate();

        return {
            isVaild: _.every(valid),
            values: this.values,
            valuesToSave: this.valuesToSave
        };
    }
}
